import { MessageSquareOff } from 'lucide-react';

import {
  BasicTableSettings,
  RefreshableTableSettings,
  createPersistedStore,
  refreshableSettings,
} from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import {
  Datatable,
  TableSettingsMenu,
  TableSettingsMenuAutoRefresh,
} from '@@/datatables';
import { confirmDelete } from '@@/modals/confirm';

import { createColumns } from './columns';
import { useSilencedAlerts, useDeleteSilence } from './useSilencedAlerts';
import { transformToAlertRuleRow } from './utils';

interface TableSettings extends BasicTableSettings, RefreshableTableSettings {}

const storageKey = 'portainer.silenced-alerts';
const settingsStore = createPersistedStore<TableSettings>(
  storageKey,
  // default sort by 'endsAt', with the closest times first
  'endsAt',
  (set) => ({
    // 10s default refresh rate
    ...refreshableSettings(set, 10 * 1000),
  })
);

export function SilencedAlertsDatatable() {
  // get silenced alerts and transform them into row data
  const tableState = useTableState(settingsStore, storageKey);
  const silencedAlertsQuery = useSilencedAlerts({
    refetchInterval: tableState.autoRefreshRate
      ? tableState.autoRefreshRate * 1000
      : undefined,
    select: transformToAlertRuleRow,
  });

  const deleteSilenceMutation = useDeleteSilence();

  async function handleDeleteSilence(silenceId: string) {
    const confirmed = await confirmDelete(
      'Are you sure you want to delete this silence? This action cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    deleteSilenceMutation.mutate({
      silenceId,
      // TODO: Get the actual alert manager URL from settings
      alertManagerURL: 'localhost:9093',
    });
  }

  const columns = createColumns(handleDeleteSilence);

  return (
    <Datatable
      dataset={silencedAlertsQuery.data || []}
      columns={columns}
      isLoading={silencedAlertsQuery.isLoading}
      settingsManager={tableState}
      emptyContentLabel="No silenced alerts found"
      title="Silenced Alerts"
      titleIcon={MessageSquareOff}
      disableSelect
      renderTableSettings={() => (
        <TableSettingsMenu>
          <TableSettingsMenuAutoRefresh
            onChange={tableState.setAutoRefreshRate}
            value={tableState.autoRefreshRate}
          />
        </TableSettingsMenu>
      )}
      data-cy="silenced-alerts-datatable"
    />
  );
}
